import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { legacyRouteMatchers, routerPaths } from "@/ui/router/routerPaths";
import { getProjectByDealId } from "@/apis/odin/routers/apiDeals";

const buildTarget = (
    databaseId: number,
    projectId: number,
    legacyRemainder: string,
): string => {
    const parts = legacyRemainder.split("/").filter(Boolean);
    if (parts.length === 0 || parts[0] === "documents") {
        return routerPaths.projects.read({
            databaseId: String(databaseId),
            projectId: String(projectId),
            tab: "documents",
        });
    }
    if (parts[0] === "operating-statement") {
        return routerPaths.projects.read({
            databaseId: String(databaseId),
            projectId: String(projectId),
            tab: "operating-statement",
        });
    }
    if (
        parts[0] === "detail" &&
        parts[1] !== undefined &&
        parts[1] !== null &&
        parts[1] !== ""
    ) {
        return routerPaths.projects.detail({
            databaseId: String(databaseId),
            projectId: String(projectId),
            tab: parts[1] as "properties" | "description",
        });
    }
    if (parts[0] === "activity") {
        return routerPaths.projects.activity({
            databaseId: String(databaseId),
            projectId: String(projectId),
        });
    }
    if (
        parts[0] === "extraction" &&
        parts[1] !== undefined &&
        parts[1] !== null &&
        parts[1] !== ""
    ) {
        const extractionId = parts[1];
        const isReview = parts[2] === "review";
        return isReview
            ? routerPaths.extraction.review({
                  databaseId: String(databaseId),
                  projectId: String(projectId),
                  extractionId: String(extractionId),
              })
            : routerPaths.extraction.mappings({
                  databaseId: String(databaseId),
                  projectId: String(projectId),
                  extractionId: String(extractionId),
              });
    }
    if (parts[0] === "files" && parts[1] === "upload") {
        return routerPaths.files.upload.url({
            projectId: projectId,
            databaseId: databaseId,
        });
    }
    return routerPaths.projects.read({
        databaseId: String(databaseId),
        projectId: String(projectId),
        tab: "documents",
    });
};

export default function LegacyDealsRedirect(): JSX.Element | null {
    const { dealId } = useParams();
    const location = useLocation();
    const [to, setTo] = React.useState<string | null>(null);

    const callGetProjectByDealId: (
        dealId: number,
    ) => Promise<{ databaseId: number; projectId: number | null }> =
        getProjectByDealId as unknown as (
            dealId: number,
        ) => Promise<{ databaseId: number; projectId: number | null }>;

    React.useEffect(() => {
        void (async () => {
            // Detect dealId if not provided by route, via legacy matchers
            let matchedDealId: string | undefined = dealId;
            let remainder = "";

            const dealsMatch = location.pathname.match(
                legacyRouteMatchers.dealsBase,
            );
            if (dealsMatch && dealsMatch.groups) {
                const matched = dealsMatch.groups["dealId"];
                matchedDealId =
                    matchedDealId ??
                    (matched !== undefined && matched !== null && matched !== ""
                        ? matched
                        : undefined);
                const rest = dealsMatch.groups["rest"];
                remainder = rest !== undefined && rest !== null ? rest : "";
            }
            if (
                matchedDealId === undefined ||
                matchedDealId === null ||
                matchedDealId === ""
            ) {
                setTo(routerPaths.main.default());
                return;
            }

            const numericDealId = Number(matchedDealId);
            if (Number.isNaN(numericDealId)) {
                setTo(routerPaths.main.default());
                return;
            }

            try {
                const { databaseId, projectId } =
                    await callGetProjectByDealId(numericDealId);
                if (projectId == null) {
                    setTo(routerPaths.main.default());
                    return;
                }
                const base = buildTarget(databaseId, projectId, remainder);
                const searchPart =
                    location.search !== undefined && location.search !== null
                        ? location.search
                        : "";
                const hashPart =
                    location.hash !== undefined && location.hash !== null
                        ? location.hash
                        : "";
                const suffix = `${searchPart}${hashPart}`;
                setTo(`${base}${suffix}`);
            } catch {
                setTo(routerPaths.main.default());
            }
        })();
    }, [
        dealId,
        location.pathname,
        location.search,
        location.hash,
        callGetProjectByDealId,
    ]);

    if (to === null) return null;
    return <Navigate to={to} replace />;
}
